import React, { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useApi } from '../hooks/useApi';
import type { User, UserPayload } from '../services/usersService';
import CheckIcon from '@mui/icons-material/Check';
import AddIcon from '@mui/icons-material/Add';
import ErrorIcon from '@mui/icons-material/Error';
import GroupIcon from '@mui/icons-material/Group';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CloseIcon from '@mui/icons-material/Close';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import LastPageIcon from '@mui/icons-material/LastPage';
import SaveIcon from '@mui/icons-material/Save';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const PERMISSION_OPTIONS = [
  'Administrators', 'Products', 'Scrappers', 'Items', 'DomainSelectors',
  'Comparison', 'PriceChanges', 'ErrorLog', 'Upload', 'HsnGrade',
  'ProductModifyLog',
];

const Users: React.FC = () => {
  const api = useApi();
  const [users, setUsers] = useState<User[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<Partial<User> | null>(null);
  const [password, setPassword] = useState('');
  const [toastMsg, setToastMsg] = useState<string | null>(null);

  const showToast = useCallback((msg: string) => {
    setToastMsg(msg);
    setTimeout(() => setToastMsg(null), 3000);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.users.list(page);
      setUsers(res.users || []);
      setTotal(res.total || 0);
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [api, page]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing({ username: '', modules: '', admin_status: 1, isAdmin: 0 });
    setPassword('');
    setShowModal(true);
  };

  const openEdit = (u: User) => {
    setEditing({ ...u });
    setPassword('');
    setShowModal(true);
  };

  const save = async () => {
    if (!editing) return;
    try {
      const permissions = (editing.modules || '').split(',').map((s) => s.trim()).filter(Boolean);
      const subModules = editing.sub_modules ? JSON.parse(editing.sub_modules) : null;
      const payload: UserPayload = {
        username: editing.username || '',
        isAdmin: !!editing.isAdmin,
        permissions,
        subModules,
      };
      if (password) payload.password = password;

      if (editing.id) {
        await api.users.update(editing.id, payload);
      } else {
        await api.users.create(payload);
      }
      setShowModal(false);
      setEditing(null);
      showToast(editing.id ? 'User updated' : 'User created');
      await load();
    } catch (err) {
      showToast((err as Error).message);
    }
  };

  const remove = async (id: number) => {
    if (!window.confirm('Delete this user?')) return;
    try {
      await api.users.remove(id);
      showToast('User deleted');
      await load();
    } catch (err) {
      showToast((err as Error).message);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / 20));

  return (
    <div className="space-y-4 px-4 mt-4">
      {/* Toast — portaled to <body> and rendered top-right at all viewports.
          Class string simplified: the previous `bottom-4` + `sm:top-4` cascade stretched the fixed element
          from top to bottom of the viewport, producing a full-page-height strip. Top-right only now. */}
      {toastMsg && typeof document !== 'undefined' && createPortal(
        <div className="fixed top-4 right-4 z-50 bg-sidebar-bg text-white px-4 py-3 rounded-xl shadow-xl border border-primary-fixed-dim/30 flex items-center gap-3 animate-toast-in">
          <CheckIcon className="text-success text-[16px] sm:text-[20px]" />
          <span className="text-xs font-medium">{toastMsg}</span>
        </div>,
        document.body
      )}

      <div className="space-y-3 sm:space-y-4">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <h1 className="font-headline-md sm:font-headline-lg font-bold text-on-surface">User Management</h1>
          <button
            onClick={openCreate}
            className="flex items-center gap-1.5 sm:gap-2 bg-primary text-on-primary px-3 sm:px-4 py-2 rounded-lg text-body-xs sm:text-body-sm font-medium hover:bg-primary/90 transition-colors"
          >
            <AddIcon className="text-[16px] sm:text-[16px]" />
            Add User
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-danger/10 border border-danger/20 rounded-lg px-4 py-3 flex items-center gap-3">
            <ErrorIcon className="text-danger text-[20px]" />
            <span className="text-body-sm text-danger">{error}</span>
          </div>
        )}

        {/* Table */}
        <div className="bg-white border border-border-subtle rounded-xl overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : users.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-on-surface-variant">
              <GroupIcon className="text-[36px] mb-2 opacity-50" />
              <p className="text-body-sm">No users found</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto min-w-0">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="border-b border-border-subtle bg-surface-container-low">
                      <th className="py-2.5 px-3 text-[10px] font-label-caps text-on-surface-variant uppercase tracking-wider hidden sm:table-cell">ID</th>
                      <th className="py-2.5 px-3 text-[10px] font-label-caps text-on-surface-variant uppercase tracking-wider">Username</th>
                      <th className="py-2.5 px-3 text-[10px] font-label-caps text-on-surface-variant uppercase tracking-wider hidden lg:table-cell">Permissions</th>
                      <th className="py-2.5 px-3 text-[10px] font-label-caps text-on-surface-variant uppercase tracking-wider">Status</th>
                      <th className="py-2.5 px-3 text-[10px] font-label-caps text-on-surface-variant uppercase tracking-wider hidden md:table-cell">Admin</th>
                      <th className="py-2.5 px-3 text-[10px] font-label-caps text-on-surface-variant uppercase tracking-wider hidden sm:table-cell">Created</th>
                      <th className="py-2.5 px-3 text-[10px] font-label-caps text-on-surface-variant uppercase tracking-wider text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="font-body-sm text-[13px]">
                    {users.map((u) => {
                      const perms = (u.modules || '').split(',').map((s) => s.trim()).filter(Boolean);
                      return (
                        <tr key={u.id} className="border-b border-border-subtle last:border-0 hover:bg-surface-container-lowest transition-colors">
                          <td className="py-2.5 px-3 font-mono text-on-surface hidden sm:table-cell">{u.id}</td>
                          <td className="py-2.5 px-3 font-medium text-on-surface">{u.username}</td>
                          <td className="py-2.5 px-3 hidden lg:table-cell">
                            <div className="flex flex-wrap gap-1">
                              {perms.slice(0, 2).map((p) => (
                                <span key={p} className="bg-surface-container px-1.5 py-0.5 rounded text-[10px] text-on-surface-variant">{p}</span>
                              ))}
                              {perms.length > 2 && (
                                <span className="bg-surface-container px-1.5 py-0.5 rounded text-[10px] text-on-surface-variant">+{perms.length - 2}</span>
                              )}
                            </div>
                          </td>
                          <td className="py-2.5 px-3">
                            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold ${u.admin_status === 1 ? 'bg-success/10 text-success' : 'bg-surface-container text-on-surface-variant'}`}>
                              {u.admin_status === 1 ? (
                                <><CheckCircleIcon className="text-[10px]" /> <span className="hidden xs:inline">Active</span></>
                              ) : (
                                <>Disabled</>
                              )}
                            </span>
                          </td>
                          <td className="py-2.5 px-3 hidden md:table-cell">
                            {u.isAdmin ? (
                              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-warning/10 text-warning">
                                <AdminPanelSettingsIcon className="text-[10px]" /> Admin
                              </span>
                            ) : null}
                          </td>
                          <td className="py-2.5 px-3 text-on-surface-variant font-mono text-[11px] hidden sm:table-cell">{u.created_at?.slice(0, 10)}</td>
                          <td className="py-2.5 px-3 text-right">
                            <div className="flex items-center justify-end gap-1 sm:gap-2">
                              <button
                                onClick={() => openEdit(u)}
                                className="p-1 sm:p-1.5 rounded hover:bg-surface-container transition-colors text-on-surface-variant hover:text-primary"
                                title="Edit"
                              >
                                <EditIcon className="text-[14px] sm:text-[16px]" />
                              </button>
                              <button
                                onClick={() => remove(u.id)}
                                className="p-1 sm:p-1.5 rounded hover:bg-surface-container transition-colors text-on-surface-variant hover:text-danger"
                                title="Delete"
                              >
                                <DeleteIcon className="text-[14px] sm:text-[16px]" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="flex flex-col sm:flex-row items-center justify-between gap-2 px-3 sm:px-4 py-3 border-t border-border-subtle bg-surface-container-low">
                <span className="text-body-xs sm:text-body-sm text-on-surface-variant">
                  Page {page}/{totalPages} ({total} total)
                </span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setPage(1)}
                    disabled={page <= 1}
                    className="w-8 h-8 rounded-lg text-body-xs border border-border-subtle text-on-surface-variant hover:bg-surface-container-low disabled:opacity-40 disabled:cursor-not-allowed transition-colors hidden sm:flex items-center justify-center"
                    title="First page"
                  >
                    <FirstPageIcon className="text-[16px]" />
                  </button>
                  <button
                    disabled={page <= 1}
                    onClick={() => setPage(page - 1)}
                    className="flex items-center gap-1 px-2 sm:px-3 py-1.5 rounded-lg text-body-xs sm:text-body-sm border border-border-subtle text-on-surface-variant hover:bg-surface-container-low disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ArrowBackIcon className="text-[14px] sm:text-[16px]" />
                    <span className="hidden sm:inline">Prev</span>
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
                    .reduce<(number | '...')[]>((acc, p, idx, arr) => {
                      if (idx > 0 && (p as number) - (arr[idx - 1] as number) > 1) acc.push('...');
                      acc.push(p);
                      return acc;
                    }, [])
                    .map((p, idx) =>
                      p === '...' ? (
                        <span key={`ellipsis-${idx}`} className="w-8 h-8 flex items-center justify-center text-body-xs text-on-surface-variant">...</span>
                      ) : (
                        <button
                          key={p}
                          onClick={() => setPage(p as number)}
                          className={`w-8 h-8 rounded-lg text-body-xs border transition-colors ${p === page
                              ? 'bg-primary text-on-primary border-primary'
                              : 'border-border-subtle text-on-surface-variant hover:bg-surface-container-low'
                            }`}
                        >
                          {p}
                        </button>
                      )
                    )}
                  <button
                    disabled={page >= totalPages}
                    onClick={() => setPage(page + 1)}
                    className="flex items-center gap-1 px-2 sm:px-3 py-1.5 rounded-lg text-body-xs sm:text-body-sm border border-border-subtle text-on-surface-variant hover:bg-surface-container-low disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <span className="hidden sm:inline">Next</span>
                    <ArrowForwardIcon className="text-[14px] sm:text-[16px]" />
                  </button>
                  <button
                    onClick={() => setPage(totalPages)}
                    disabled={page >= totalPages}
                    className="w-8 h-8 rounded-lg text-body-xs border border-border-subtle text-on-surface-variant hover:bg-surface-container-low disabled:opacity-40 disabled:cursor-not-allowed transition-colors hidden sm:flex items-center justify-center"
                    title="Last page"
                  >
                    <LastPageIcon className="text-[16px]" />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Add/Edit Modal — portaled to <body> to escape the space-y-4 wrapper,
          which would otherwise inject a 16px margin-top on the fixed layer. */}
      {showModal && editing && typeof document !== 'undefined' && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/50 backdrop-blur-sm" onClick={() => setShowModal(false)}>
          <div className="bg-surface rounded-xl shadow-xl w-full max-w-md border border-border-subtle animate-toast-in" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 sm:px-5 py-3 sm:py-4 border-b border-border-subtle">
              <h3 className="font-headline-sm text-on-surface">{editing.id ? 'Edit User' : 'Add User'}</h3>
              <button onClick={() => setShowModal(false)} className="p-1 rounded hover:bg-surface-container transition-colors">
                <CloseIcon className="text-on-surface-variant text-[20px]" />
              </button>
            </div>

            <div className="p-4 sm:p-5 space-y-3 sm:space-y-4">
              {/* Username */}
              <div>
                <label className="text-[10px] font-label-caps text-on-surface-variant uppercase block mb-1.5">Username</label>
                <input
                  type="text"
                  value={editing.username || ''}
                  onChange={(e) => setEditing({ ...editing, username: e.target.value })}
                  className="w-full bg-white border border-border-subtle rounded-lg px-3 py-2 text-body-sm text-on-surface focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                  placeholder="Enter username"
                />
              </div>

              {/* Password */}
              <div>
                <label className="text-[10px] font-label-caps text-on-surface-variant uppercase block mb-1.5">
                  Password{editing.id ? ' (leave blank to keep)' : ''}
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-white border border-border-subtle rounded-lg px-3 py-2 text-body-sm text-on-surface focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                  placeholder={editing.id ? '••••••••' : 'Enter password'}
                />
              </div>

              {/* Permissions */}
              <div>
                <label className="text-[10px] font-label-caps text-on-surface-variant uppercase block mb-1.5">Permissions</label>
                <div className="bg-white border border-border-subtle rounded-lg p-2 sm:p-3 max-h-40 sm:max-h-48 overflow-y-auto space-y-1">
                  {PERMISSION_OPTIONS.map((p) => {
                    const currentPerms = (editing.modules || '').split(',').map((s) => s.trim()).filter(Boolean);
                    const isChecked = currentPerms.includes(p);
                    return (
                      <label key={p} className="flex items-center gap-2 cursor-pointer hover:bg-surface-container-low rounded px-1.5 py-0.5">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={(e) => {
                            const newPerms = e.target.checked
                              ? [...currentPerms, p]
                              : currentPerms.filter((x) => x !== p);
                            setEditing({ ...editing, modules: newPerms.join(',') });
                          }}
                          className="w-4 h-4 rounded border-border-subtle text-primary focus:ring-primary/20 accent-primary"
                        />
                        <span className="text-body-xs sm:text-body-sm text-on-surface">{p}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Toggles */}
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!editing.isAdmin}
                    onChange={(e) => setEditing({ ...editing, isAdmin: e.target.checked ? 1 : 0 })}
                    className="w-4 h-4 rounded border-border-subtle text-primary focus:ring-primary/20 accent-primary"
                  />
                  <span className="text-body-xs sm:text-body-sm text-on-surface font-medium">Is Admin</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!editing.admin_status}
                    onChange={(e) => setEditing({ ...editing, admin_status: e.target.checked ? 1 : 0 })}
                    className="w-4 h-4 rounded border-border-subtle text-primary focus:ring-primary/20 accent-primary"
                  />
                  <span className="text-body-xs sm:text-body-sm text-on-surface font-medium">Active</span>
                </label>
              </div>
            </div>

            <div className="px-4 sm:px-5 py-3 sm:py-4 border-t border-border-subtle flex gap-2 sm:gap-3">
              <button
                onClick={() => setShowModal(false)}
                className="flex-1 bg-white border border-border-subtle py-2 rounded-lg text-body-xs sm:text-body-sm font-medium text-on-surface hover:bg-surface-container-low transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={save}
                disabled={!editing.username}
                className="flex-1 bg-primary-container text-on-primary py-2 rounded-lg text-body-xs sm:text-body-sm font-semibold hover:bg-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1.5 sm:gap-2"
              >
                <SaveIcon className="text-[14px] sm:text-[16px]" />
                Save
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default Users;
